import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { derivedOutcome, flattenItems, type SPINE_BINDING_KEYS } from "@njbsoft/form-logic";
import {
  recordBaseFromTarget,
  type BundleTarget,
  type ChangeRequestCategoryTemplate,
  type DraftSidecar,
  type StorageAdapter,
  type StoredAssignment,
  type StoredDraft,
  type StoredFacility,
  type StoredKit,
  type StoredTemplateVersion,
  type StoredUtilityRequirements,
} from "@njbsoft/field-sync";
import { uuidv7 } from "@njbsoft/utils";
import { Button, EmptyState, PageSkeleton } from "@/components/ui";
import { DraftLockedElsewhere } from "@/components/draft-locked-elsewhere";
import {
  type ReplaceRequest,
  type ReplaceStep,
} from "@/components/tester/change-requests/staging/replace-model";
import { ReplaceFlow } from "@/components/tester/change-requests/staging/replace-steps";
import { useDraftSidecar } from "@/components/tester/change-requests/staging/use-draft-sidecar";
import { EMPTY_ASSIGNMENT_SIDECAR } from "@/components/tester/execution/device-of-record-facts";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";
import { parseStoredDefinition } from "@/lib/fieldwork/stored-definition";
import { useDraftLock } from "@/hooks/use-draft-lock";
import { useIsOnline } from "@/hooks/use-is-online";

type AssignmentSidecar = Extract<DraftSidecar, { kind: "assignment" }>;

interface ReplaceSearch {
  step?: ReplaceStep;
}

const STEPS: readonly ReplaceStep[] = ["assembly", "test", "review"];

export const Route = createFileRoute("/_company/my-work/$assignmentId/replace")({
  validateSearch: (search: Record<string, unknown>): ReplaceSearch => ({
    step: STEPS.find((step) => step === search.step),
  }),
  component: AssignmentReplacePage,
});

const PERFORMED_AT: (typeof SPINE_BINDING_KEYS)[number] = "test.performed_at";

/** One atomic settle of the local reads — no partial renders. */
interface LocalReads {
  row: StoredAssignment | null;
  target: BundleTarget | null;
  pinned: StoredTemplateVersion | null;
  facility: StoredFacility | null;
  draft: StoredDraft | null;
  requirements: StoredUtilityRequirements | null;
  kits: StoredKit[];
  template: ChangeRequestCategoryTemplate | null;
}

/**
 * Replace device beside this assignment's test: everything reads from the
 * device store, under the same draft lock as the test screen. Save replacement
 * stages the request on the assignment's sidecar and returns to the test,
 * which sends everything together. The unavailable gate (no stored row, or a
 * target with no installation) mirrors the test screen's: online it fetches
 * itself ONCE on mount, and the Sync Now button is the offline affordance and
 * the retry.
 */
function AssignmentReplacePage() {
  const { t } = useTranslation(["change-requests", "field-work"]);
  const { assignmentId } = Route.useParams();
  const { storage, engine } = useFieldworkStorage();
  const draftLock = useDraftLock(assignmentId);
  const [reads, setReads] = useState<LocalReads | null>(null);

  const refreshLocal = useCallback(async () => {
    if (storage === null) return;
    const [row, draft, kits] = await Promise.all([
      storage.getBundleAssignment(assignmentId),
      storage.getDraft(assignmentId),
      storage.listBundleKits(),
    ]);
    if (row === null) {
      setReads({
        row,
        target: null,
        pinned: null,
        facility: null,
        draft,
        requirements: null,
        kits,
        template: null,
      });
      return;
    }
    const { org_id: orgId, facility_id: facilityId } = row.assignment;
    const [target, pinned, facility, requirements, template] = await Promise.all([
      storage.getTarget(facilityId, row.assignment.device_installation_id),
      storage.getTemplateVersion(row.assignment.template_version_id),
      storage.getBundleFacility(facilityId),
      storage.getUtilityRequirements(orgId),
      storage.getCategoryTemplate(orgId, "testable_assembly"),
    ]);
    setReads({
      row,
      target,
      pinned,
      facility,
      draft,
      requirements,
      kits,
      template,
    });
  }, [storage, assignmentId]);

  // Read only once this tab owns the draft: the page writes the sidecar the
  // test screen's panel reads.
  useEffect(() => {
    if (draftLock !== "held") return;
    void refreshLocal();
  }, [refreshLocal, draftLock]);

  // The unavailable gate's call-to-action: run the engine, then re-read so the
  // screen reflects the run's outcome.
  const handleSync = useCallback(() => {
    if (engine === null) return;
    void engine.syncNow().then(() => refreshLocal());
  }, [engine, refreshLocal]);

  // Derived before the render gates so the online self-fetch below keys on
  // the same predicate the gate renders (the render gate spells its own copy
  // inline — that is what narrows the loaded arm's reads for the typechecker).
  const rowUnavailable =
    reads !== null &&
    (reads.row === null ||
      reads.target === null ||
      reads.target.device_installation_id === undefined);

  // The gate's online self-fetch: once per mount, never on a re-render and
  // never again after a failed run — the button is the retry, and offline it
  // is the only affordance.
  const isOnline = useIsOnline();
  const autoFetchFired = useRef(false);
  useEffect(() => {
    if (!rowUnavailable || !isOnline || autoFetchFired.current) return;
    autoFetchFired.current = true;
    handleSync();
  }, [rowUnavailable, isOnline, handleSync]);

  if (draftLock === "heldElsewhere") return <DraftLockedElsewhere />;
  if (draftLock === "acquiring" || reads === null || storage === null) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }
  const { row, target } = reads;
  if (row === null || target === null || target.device_installation_id === undefined) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("replace.title")}
          description={t("field-work:execute.assignmentUnavailable")}
        />
        <div className="flex justify-center">
          <Button onClick={handleSync}>{t("field-work:sync.syncNow")}</Button>
        </div>
      </div>
    );
  }
  return (
    <AssignmentReplace
      storage={storage}
      assignmentId={assignmentId}
      installationId={target.device_installation_id}
      reads={{ ...reads, row, target }}
    />
  );
}

function AssignmentReplace({
  storage,
  assignmentId,
  installationId,
  reads,
}: {
  storage: StorageAdapter;
  assignmentId: string;
  installationId: string;
  reads: LocalReads & { row: StoredAssignment; target: BundleTarget };
}) {
  const { t } = useTranslation("change-requests");
  const handle = useDraftSidecar<"assignment">(assignmentId);
  if (handle.status === "loading") {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (handle.status === "error") {
    return (
      <p role="alert" className="p-4 text-sm text-danger sm:p-8">
        {t("replace.loadFailed")}
      </p>
    );
  }
  return (
    <AssignmentReplaceSession
      storage={storage}
      assignmentId={assignmentId}
      installationId={installationId}
      reads={reads}
      sidecar={handle.sidecar}
      update={handle.update}
    />
  );
}

function AssignmentReplaceSession({
  storage,
  assignmentId,
  installationId,
  reads,
  sidecar,
  update,
}: {
  storage: StorageAdapter;
  assignmentId: string;
  installationId: string;
  reads: LocalReads & { row: StoredAssignment; target: BundleTarget };
  sidecar: AssignmentSidecar | null;
  update: (change: (current: AssignmentSidecar | null) => AssignmentSidecar) => Promise<void>;
}) {
  const { t } = useTranslation("change-requests");
  const navigate = useNavigate();
  const { step = "assembly" } = Route.useSearch();
  const { row, target, pinned, draft } = reads;
  const staged = sidecar?.stagedRequest ?? null;
  const stored: ReplaceRequest | null =
    staged !== null && staged.item.kind === "replacement" && staged.deviceInput !== null
      ? { item: staged.item, deviceInput: staged.deviceInput, stagedTests: staged.stagedTests }
      : null;
  // A request's ids are minted when the replacement opens, so photos taken
  // before its first save already stage under its client set id.
  const [ids] = useState(() =>
    stored === null || staged === null
      ? { owner: uuidv7(), requestId: uuidv7() }
      : { owner: staged.clientSetId, requestId: stored.item.client_request_id },
  );
  const predecessor = recordBaseFromTarget(target);
  const utilityName =
    reads.requirements?.orgName ?? reads.facility?.orgName ?? row.assignment.org_name ?? "";

  // Staging the replacement answers the device-of-record question in the same
  // write, so the answer and the request never disagree. Another kind's staged
  // request is the panel's to remove first; overwriting it here would orphan
  // its photos, so it is refused.
  const writeRequest = useCallback(
    (change: (current: ReplaceRequest | null) => ReplaceRequest) =>
      update((current) => {
        const base = current ?? EMPTY_ASSIGNMENT_SIDECAR;
        const prior = base.stagedRequest;
        if (prior !== null && prior.item.kind !== "replacement") {
          throw new Error(`staged_request_of_another_kind:${prior.item.kind}`);
        }
        const request =
          prior !== null && prior.deviceInput !== null
            ? { item: prior.item, deviceInput: prior.deviceInput, stagedTests: prior.stagedTests }
            : null;
        const next = change(request);
        return {
          ...base,
          deviceOfRecord: { answer: "replacement", record_version: predecessor.record_version },
          answeredAt:
            base.deviceOfRecord?.answer === "replacement"
              ? base.answeredAt
              : new Date().toISOString(),
          stagedRequest: { clientSetId: ids.owner, ...next },
          removalSetAside: false,
        };
      }),
    [update, ids.owner, predecessor.record_version],
  );

  const finish = useCallback(async () => {
    await navigate({ to: "/my-work/$assignmentId/execute", params: { assignmentId } });
  }, [navigate, assignmentId]);

  const pinnedDefinition = pinned === null ? null : parseStoredDefinition(pinned.definitionJson);
  const bound =
    pinnedDefinition === null
      ? undefined
      : flattenItems(pinnedDefinition).ordered.find((item) => item.binding === PERFORMED_AT);
  const performedAt = bound === undefined ? undefined : draft?.answers[bound.link_id];
  const address = [
    target.address_line1,
    [target.city, target.state_code, target.zip_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const toExecute = () => {
    void navigate({ to: "/my-work/$assignmentId/execute", params: { assignmentId } });
  };

  return (
    <ReplaceFlow
      anchor="assignment"
      step={step}
      onStep={(next) => {
        void navigate({
          to: "/my-work/$assignmentId/replace",
          params: { assignmentId },
          search: { step: next },
        });
      }}
      storage={storage}
      orgId={row.assignment.org_id}
      utilityName={utilityName}
      facility={{
        id: row.assignment.facility_id,
        name: target.facility_name,
        addressLine: address,
        orgName: utilityName,
      }}
      bundleFacility={reads.facility}
      predecessor={predecessor}
      predecessorInstallationId={installationId}
      owner={ids.owner}
      requestId={ids.requestId}
      stored={stored}
      template={reads.template}
      requirements={reads.requirements}
      kits={reads.kits}
      oldTest={{
        formTitle: pinned?.title ?? "",
        outcome:
          pinnedDefinition === null || draft === null
            ? null
            : derivedOutcome(pinnedDefinition, draft.answers, {
                ...reads.facility?.readContext,
                ...target.device_read_context,
              }),
        performedAt: typeof performedAt === "string" ? performedAt : null,
        rejected: sidecar?.pinnedRejection ?? null,
      }}
      reuse={
        draft === null
          ? null
          : {
              assignmentId,
              answers: draft.answers,
              pinnedVersionId: row.assignment.template_version_id,
              testKitId: draft.testKitId,
            }
      }
      resubmission={null}
      initialKitId={draft?.testKitId ?? null}
      kitNote={t("replace.test.sameKit", { serial: predecessor.serial_number ?? "" })}
      backTo={`/my-work/${assignmentId}/execute`}
      backLabel={t("replace.backToTest")}
      writeRequest={writeRequest}
      finish={finish}
      finishLabel={t("replace.saveReplacement")}
      onCancel={toExecute}
      onOpenOldTest={toExecute}
    />
  );
}
